import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../../components/EmptyState';
import {
  Plus,
  FileText,
  MoreHorizontal,
  ExternalLink,
  BarChart3,
  TrendingUp,
  Copy,
  Check,
  Trash2,
  Edit,
  Globe,
  Eye,
  Users,
  Search,
  ArrowRight,
  Folder,
  FolderPlus,
  FolderOpen,
  ChevronRight,
  Home,
  FolderInput,
  Pencil,
  Loader2,
  LayoutTemplate,
} from 'lucide-react';
import useFormList from '../../hooks/useFormList';
import useFormTemplates from '../../hooks/useFormTemplates';
import FormTemplateGallery from '../../components/FormTemplateGallery';
import SkeletonFormList from '../../components/skeletons/SkeletonFormList';

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ---------- Card Context Menu ----------
function CardActions({ form, onEdit, onRename, onResponses, onAnalytics, onPublicUrl, onCopyLink, copiedFormId, onPublish, onUnpublish, onDuplicate, onDelete, onMoveToFolder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        className="p-1.5 rounded-md text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg bg-white shadow-lg border border-gray-100 py-1">
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onEdit(form.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <Edit className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Edit
          </button>
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onRename(form); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <Pencil className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Rename
          </button>
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onResponses(form.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <BarChart3 className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Responses
          </button>
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onAnalytics(form.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <TrendingUp className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Analytics
          </button>
          <div className="my-1 border-t border-gray-100" />
          {form.status === 'published' && (
            <>
              <a href={onPublicUrl(form.id)} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); setOpen(false); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                <ExternalLink className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Preview Public
              </a>
              <button onClick={(e) => { e.stopPropagation(); onCopyLink(form.id); setOpen(false); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                {copiedFormId === form.id ? <Check className="h-3.5 w-3.5 mr-2.5 text-brand-600" /> : <Copy className="h-3.5 w-3.5 mr-2.5 text-gray-400" />}
                {copiedFormId === form.id ? 'Copied!' : 'Copy Link'}
              </button>
            </>
          )}
          {form.status === 'draft' ? (
            <button onClick={(e) => { e.stopPropagation(); setOpen(false); onPublish(form.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
              <Globe className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Publish
            </button>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); setOpen(false); onUnpublish(form.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
              <Globe className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Unpublish
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onDuplicate(form.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <Copy className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Duplicate
          </button>
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onMoveToFolder(form.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <FolderInput className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Move to Folder
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(form.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5 mr-2.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Folder Context Menu ----------
function FolderActions({ folder, onRename, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        className="p-1 rounded-md text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100"
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-40 rounded-lg bg-white shadow-lg border border-gray-100 py-1">
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onRename(folder); }} className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <Pencil className="h-3.5 w-3.5 mr-2.5 text-gray-400" /> Rename
          </button>
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(folder.id); }} className="flex items-center w-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5 mr-2.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

function MetricPill({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1 xl:gap-1.5 text-xs text-gray-500 min-w-0" title={`${value ?? '\u2014'} ${label}`}>
      <Icon className="h-3 w-3 text-gray-400 flex-shrink-0" />
      <span className="text-gray-900 font-medium whitespace-nowrap">{value ?? '\u2014'}</span>
      <span className="text-gray-400 hidden xl:inline text-[10px] truncate">{label}</span>
    </div>
  );
}

// ---------- Move to Folder Modal ----------
function MoveToFolderModal({ formId, allFolders, currentFolderId, onMove, onClose }) {
  const modalRef = useRef(null);
  const [selectedFolderId, setSelectedFolderId] = useState(undefined); // undefined = unchanged

  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Build tree structure from flat folder list
  const buildTree = (folders) => {
    const map = {};
    const roots = [];
    folders.forEach((f) => { map[f.id] = { ...f, children: [] }; });
    folders.forEach((f) => {
      if (f.parent_id && map[f.parent_id]) {
        map[f.parent_id].children.push(map[f.id]);
      } else {
        roots.push(map[f.id]);
      }
    });
    return roots;
  };

  const tree = buildTree(allFolders);

  const renderNode = (node, depth = 0) => {
    const isSelected = selectedFolderId === node.id;
    const isCurrent = currentFolderId === node.id;
    return (
      <div key={node.id}>
        <button
          onClick={() => setSelectedFolderId(node.id)}
          className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
            isSelected
              ? 'bg-brand-50 text-brand-700 font-medium'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
          style={{ paddingLeft: `${12 + depth * 20}px` }}
        >
          {isSelected ? (
            <FolderOpen className="h-4 w-4 text-brand-500 flex-shrink-0" />
          ) : (
            <Folder className="h-4 w-4 text-gray-400 flex-shrink-0" />
          )}
          <span className="truncate">{node.name}</span>
          {isCurrent && (
            <span className="ml-auto text-[10px] text-gray-400 font-medium flex-shrink-0">current</span>
          )}
        </button>
        {node.children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  const handleMove = () => {
    if (selectedFolderId !== undefined) {
      onMove(formId, selectedFolderId);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div ref={modalRef} className="w-full max-w-sm mx-4 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-base font-semibold text-gray-900">Move to Folder</h3>
          <p className="mt-0.5 text-sm text-gray-500">Select a destination folder.</p>
        </div>
        <div className="px-3 pb-2 max-h-64 overflow-y-auto">
          {/* Root option */}
          <button
            onClick={() => setSelectedFolderId(null)}
            className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
              selectedFolderId === null
                ? 'bg-brand-50 text-brand-700 font-medium'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Home className="h-4 w-4 flex-shrink-0" />
            <span>Root (no folder)</span>
            {!currentFolderId && (
              <span className="ml-auto text-[10px] text-gray-400 font-medium flex-shrink-0">current</span>
            )}
          </button>
          {tree.map((node) => renderNode(node))}
          {allFolders.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No folders yet. Create one first.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-3.5 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={selectedFolderId === undefined}
            className="px-3.5 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Move
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Create / Rename Folder Modal ----------
function FolderNameModal({ title, initialName, placeholder, onSubmit, onClose }) {
  const modalRef = useRef(null);
  const [name, setName] = useState(initialName || '');

  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onSubmit(name.trim());
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div ref={modalRef} className="w-full max-w-sm mx-4 bg-white rounded-xl shadow-lg border border-gray-100 p-5">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <form onSubmit={handleSubmit} className="mt-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder || "Folder name"}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-shadow"
            required
            ref={(el) => { if (el) requestAnimationFrame(() => el.focus()); }}
          />
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3.5 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
            >
              {initialName ? 'Rename' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ========== MAIN COMPONENT ==========
export default function AbleFormList() {
  const navigate = useNavigate();
  const {
    forms, loading, formAnalytics, showCreateModal, newFormTitle, newFormDescription,
    copiedFormId, highlightedFormId,
    setShowCreateModal, setNewFormTitle, setNewFormDescription,
    createForm, createFormFromTemplate, deleteForm, publishForm, unpublishForm, copyPublicLink,
    getPublicUrl, duplicateForm, renameForm,
    // Folder
    folders, currentFolderId, breadcrumb,
    createFolder, renameFolder, deleteFolder, moveFormToFolder,
    navigateToFolder, navigateToRoot, fetchAllFolders,
  } = useFormList();

  const {
    templates, categories, loading: templatesLoading,
    applyTemplate, deleteTemplate: deleteFormTemplate,
  } = useFormTemplates();

  const [searchQuery, setSearchQuery] = useState('');
  const [navigatingFormId, setNavigatingFormId] = useState(null);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [showCreateChoice, setShowCreateChoice] = useState(false);
  const modalRef = useRef(null);

  // Folder modals
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(null); // folder object
  const [renamingForm, setRenamingForm] = useState(null); // form object
  const [movingFormId, setMovingFormId] = useState(null);
  const [allFoldersForModal, setAllFoldersForModal] = useState([]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setShowCreateModal(false);
        setNewFormTitle('');
        setNewFormDescription('');
      }
    }
    if (showCreateModal) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateModal, setShowCreateModal, setNewFormTitle, setNewFormDescription]);

  // When move modal opens, fetch all folders
  const openMoveModal = useCallback(async (formId) => {
    const allFolders = await fetchAllFolders();
    setAllFoldersForModal(allFolders);
    setMovingFormId(formId);
  }, [fetchAllFolders]);

  const filteredForms = forms.filter(f =>
    f.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && !forms.length) return <SkeletonFormList />;

  return (
    <div className="px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Forms</h1>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowCreateFolderModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <FolderPlus className="h-4 w-4" />
            New Folder
          </button>
          <button
            onClick={() => setShowCreateChoice(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Form
          </button>
        </div>
      </div>

      {/* Breadcrumb */}
      {(currentFolderId || breadcrumb.length > 0) && (
        <nav className="flex items-center gap-1 mb-5 text-sm">
          <button
            onClick={navigateToRoot}
            className="flex items-center gap-1 text-gray-500 hover:text-brand-600 transition-colors"
          >
            <Home className="h-3.5 w-3.5" />
            <span>Forms</span>
          </button>
          {breadcrumb.map((crumb) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
              <button
                onClick={() => navigateToFolder(crumb.id)}
                className={`truncate max-w-[160px] transition-colors ${
                  crumb.id === currentFolderId
                    ? 'text-gray-900 font-medium'
                    : 'text-gray-500 hover:text-brand-600'
                }`}
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </nav>
      )}

      {forms.length === 0 && folders.length === 0 && !currentFolderId ? (
        /* Empty state — root with nothing */
        <EmptyState
          illustration="forms"
          headline="Build your first form"
          description="Create beautiful forms to collect responses, gather feedback, and capture leads — all in one place."
          buttonLabel="Create Form"
          onAction={() => setShowCreateChoice(true)}
        />
      ) : (
        <>
          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search forms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-colors"
              />
            </div>
          </div>

          {/* Folders row */}
          {folders.length > 0 && !searchQuery && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 mb-6">
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  onClick={() => navigateToFolder(folder.id)}
                  className="group flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-brand-200 hover:shadow-sm cursor-pointer transition-all"
                >
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Folder className="w-4 h-4 text-brand-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-600 transition-colors">
                      {folder.name}
                    </p>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <FolderActions
                      folder={folder}
                      onRename={(f) => setRenamingFolder(f)}
                      onDelete={deleteFolder}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty folder state */}
          {folders.length === 0 && filteredForms.length === 0 && currentFolderId && !searchQuery && (
            <div className="text-center py-16">
              <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <FolderOpen className="w-6 h-6 text-gray-300" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">This folder is empty</h3>
              <p className="mt-1 text-sm text-gray-400 max-w-xs mx-auto">
                Create a form or subfolder, or move existing forms here.
              </p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  onClick={() => setShowCreateFolderModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <FolderPlus className="h-4 w-4" />
                  New Subfolder
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  New Form
                </button>
              </div>
            </div>
          )}

          {/* No search results */}
          {searchQuery && filteredForms.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-400">
              No forms matching &ldquo;{searchQuery}&rdquo;
            </div>
          )}

          {/* Cards grid */}
          {filteredForms.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredForms.map((form) => {
                const analytics = formAnalytics[form.id];
                const totalResponses = analytics?.total_completions ?? null;
                const conversionRate = analytics?.total_starts > 0
                  ? Math.round((analytics.total_completions / analytics.total_views) * 100)
                  : null;
                const completionRate = analytics?.completion_rate != null
                  ? Math.round(analytics.completion_rate)
                  : null;

                const isNavigating = navigatingFormId === form.id;

                return (
                  <div
                    key={form.id}
                    data-form-id={form.id}
                    onClick={() => {
                      setNavigatingFormId(form.id);
                      navigate(`/forms/${form.id}/builder`);
                    }}
                    style={{ cursor: 'pointer' }}
                    className={`group block bg-white border rounded-xl hover:shadow-md transition-all duration-200 ${
                      isNavigating
                        ? 'border-brand-200 opacity-75'
                        : highlightedFormId === form.id
                          ? 'border-brand-300 shadow-md ring-2 ring-brand-100'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* Card header */}
                    <div className="p-5 pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                            {isNavigating ? (
                              <Loader2 className="w-4 h-4 text-brand-600 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4 text-brand-600" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-brand-600 transition-colors">
                              {form.title}
                            </h3>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {form.updated_at
                                ? `Edited ${timeAgo(form.updated_at)}`
                                : `Created ${timeAgo(form.created_at)}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            form.status === 'published'
                              ? 'bg-brand-50 text-brand-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {form.status === 'published' ? 'Live' : 'Draft'}
                          </span>
                          <CardActions
                            form={form}
                            onEdit={(id) => navigate(`/forms/${id}/builder`)}
                            onRename={(f) => setRenamingForm(f)}
                            onResponses={(id) => navigate(`/forms/${id}/responses`)}
                            onAnalytics={(id) => navigate(`/forms/${id}/analytics`)}
                            onPublicUrl={getPublicUrl}
                            onCopyLink={copyPublicLink}
                            copiedFormId={copiedFormId}
                            onPublish={publishForm}
                            onUnpublish={unpublishForm}
                            onDuplicate={duplicateForm}
                            onDelete={deleteForm}
                            onMoveToFolder={openMoveModal}
                          />
                        </div>
                      </div>

                      {form.description && (
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2 ml-12">
                          {form.description}
                        </p>
                      )}
                    </div>

                    {/* Metrics bar */}
                    <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-200/60 overflow-hidden">
                      <div className="flex items-center justify-around xl:justify-start gap-3 xl:gap-4">
                        <MetricPill icon={Users} label="responses" value={totalResponses} />
                        <MetricPill icon={Eye} label="conversion" value={conversionRate != null ? `${conversionRate}%` : null} />
                        <MetricPill icon={TrendingUp} label="completion" value={completionRate != null ? `${completionRate}%` : null} />
                      </div>
                    </div>

                    {/* Quick actions footer */}
                    <div className="px-5 py-2.5 border-t border-gray-200/60 flex items-center justify-between opacity-50 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/forms/${form.id}/responses`); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        >
                          <BarChart3 className="h-3 w-3" /> Responses
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/forms/${form.id}/analytics`); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        >
                          <TrendingUp className="h-3 w-3" /> Analytics
                        </button>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-400 transition-colors" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Create Choice Modal — pick "Blank" or "Template" */}
      {showCreateChoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCreateChoice(false); }}>
          <div className="w-full max-w-md mx-4 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-900">Create New Form</h3>
            <p className="mt-1 text-sm text-gray-500">How would you like to start?</p>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={() => { setShowCreateChoice(false); setShowCreateModal(true); }}
                className="flex flex-col items-center gap-3 p-5 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50/50 transition-all group text-center"
              >
                <div className="w-11 h-11 rounded-lg bg-gray-100 group-hover:bg-brand-100 flex items-center justify-center transition-colors">
                  <FileText className="w-5 h-5 text-gray-500 group-hover:text-brand-600 transition-colors" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">Start from scratch</div>
                  <div className="text-xs text-gray-500 mt-0.5">Build your form field by field</div>
                </div>
              </button>

              <button
                onClick={() => { setShowCreateChoice(false); setShowTemplateGallery(true); }}
                className="flex flex-col items-center gap-3 p-5 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50/50 transition-all group text-center"
              >
                <div className="w-11 h-11 rounded-lg bg-gray-100 group-hover:bg-brand-100 flex items-center justify-center transition-colors">
                  <LayoutTemplate className="w-5 h-5 text-gray-500 group-hover:text-brand-600 transition-colors" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">Use a template</div>
                  <div className="text-xs text-gray-500 mt-0.5">Choose from ready-made forms</div>
                </div>
              </button>
            </div>

            <div className="flex justify-end mt-5 pt-4 border-t border-gray-100">
              <button
                onClick={() => setShowCreateChoice(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Blank Form Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div ref={modalRef} className="w-full max-w-md mx-4 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-900">Create Blank Form</h3>
            <p className="mt-1 text-sm text-gray-500">Set up your form details to get started.</p>

            <form onSubmit={createForm} className="mt-5">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                  <input
                    type="text"
                    value={newFormTitle}
                    onChange={(e) => setNewFormTitle(e.target.value)}
                    placeholder="e.g. Contact Form"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-shadow"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={newFormDescription}
                    onChange={(e) => setNewFormDescription(e.target.value)}
                    placeholder="Briefly describe what this form is for..."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 resize-none transition-shadow"
                    rows="3"
                  />
                </div>
              </div>

              {currentFolderId && breadcrumb.length > 0 && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
                  <Folder className="h-3 w-3 text-gray-400" />
                  <span>Will be created in: <span className="font-medium text-gray-700">{breadcrumb[breadcrumb.length - 1]?.name}</span></span>
                </div>
              )}

              <div className="flex justify-end gap-2.5 mt-6 pt-5 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setNewFormTitle(''); setNewFormDescription(''); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
                >
                  Create Form
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Template Gallery Modal */}
      <FormTemplateGallery
        isOpen={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        templates={templates}
        categories={categories}
        loading={templatesLoading}
        onDeleteTemplate={deleteFormTemplate}
        onSelectTemplate={async (template) => {
          const newForm = await applyTemplate(template.id);
          if (newForm) {
            createFormFromTemplate(newForm);
            setShowTemplateGallery(false);
          }
        }}
      />

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <FolderNameModal
          title="New Folder"
          onSubmit={(name) => createFolder(name)}
          onClose={() => setShowCreateFolderModal(false)}
        />
      )}

      {/* Rename Folder Modal */}
      {renamingFolder && (
        <FolderNameModal
          title="Rename Folder"
          initialName={renamingFolder.name}
          onSubmit={(name) => renameFolder(renamingFolder.id, name)}
          onClose={() => setRenamingFolder(null)}
        />
      )}

      {/* Rename Form Modal */}
      {renamingForm && (
        <FolderNameModal
          title="Rename Form"
          initialName={renamingForm.title}
          placeholder="Form name"
          onSubmit={(name) => renameForm(renamingForm.id, name)}
          onClose={() => setRenamingForm(null)}
        />
      )}

      {/* Move to Folder Modal */}
      {movingFormId && (
        <MoveToFolderModal
          formId={movingFormId}
          allFolders={allFoldersForModal}
          currentFolderId={currentFolderId}
          onMove={moveFormToFolder}
          onClose={() => setMovingFormId(null)}
        />
      )}
    </div>
  );
}
